import { useState } from "react";
import { Button } from "@/src/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/src/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { useFetch } from "@/src/lib/useFetch";
import { FormProvider, useForm } from "react-hook-form";
import { RelationSingleInput } from "./RelationSingleInput";
import { extractFromMultiName } from "../utils";

type RelocationButtonProps = {
  ids: string[];
  onRelocate?: (destinationId: string, destinationType: string) => void;
};

export function RelocationButton({ ids, onRelocate }: RelocationButtonProps) {
  const [open, setOpen] = useState(false);
  const [destinationType, setDestinationType] = useState<"location" | "town" | "country">("location");
  const { fetch } = useFetch();

  const methods = useForm({
    defaultValues: {
      destination: null as { id: string; names: string } | null,
    },
  });

  const { watch, reset } = methods;
  const selectedDestination = watch("destination")?.id;

  const loadOptions = async (query: string) => {
    if (!query) {
      return [];
    }

    try {
      let endpoint = "";
      switch (destinationType) {
        case "location":
          endpoint = `/api/locations/locations?filter=${encodeURIComponent(query)}`;
          break;
        case "town":
          endpoint = `/api/locations/towns?filter=${encodeURIComponent(query)}`;
          break;
        case "country":
          endpoint = `/api/locations/countries?filter=${encodeURIComponent(query)}`;
          break;
      }

      const response = await fetch<{ data: { id: string; names: string }[] }>(endpoint);
      return response.data.map((item) => ({
        id: item.id,
        names: extractFromMultiName(item.names),
      }));
    } catch (error) {
      console.error("Error searching destinations:", error);
      return [];
    }
  };

  const handleAccept = () => {
    if (selectedDestination && onRelocate) {
      onRelocate(selectedDestination, destinationType);
    }
    setOpen(false);
    reset();
  };

  const handleDestinationTypeChange = (value: "location" | "town" | "country") => {
    setDestinationType(value);
    reset({ destination: null });
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} disabled={ids.length === 0}>
        Relocate {ids.length > 0 ? `(${ids.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) reset();
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Relocate Items</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Destination Type</label>
              <Select value={destinationType} onValueChange={handleDestinationTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select destination type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="location">Location</SelectItem>
                  <SelectItem value="town">Town</SelectItem>
                  <SelectItem value="country">Country</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <FormProvider {...methods}>
              <RelationSingleInput
                name="destination"
                label={`${destinationType.charAt(0).toUpperCase()}${destinationType.slice(1)}`}
                loadOptions={loadOptions}
              />
            </FormProvider>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAccept} disabled={!selectedDestination}>
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
